import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { teamsApi, eventsApi } from '../../api/client';
import { ArrowLeftIcon, TrophyIcon, SwordsIcon } from '../Icons/Icons';
import './TeamStatistics.css';

const TeamStatistics = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalMatches: 0,
    wins: 0,
    losses: 0,
    draws: 0,
    winRate: 0,
    recentResults: []
  });

  useEffect(() => {
    loadData();
  }, [teamId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamRes, eventsRes] = await Promise.all([
        teamsApi.getById(teamId),
        eventsApi.getAll()
      ]);

      setTeam(teamRes.data);

      // Filtrer les événements de cette équipe qui sont des scrims ou tournois avec un score
      const teamEvents = eventsRes.data.filter(event => {
        const teamIds = event.team_ids && event.team_ids.length > 0
          ? event.team_ids
          : (event.team_id ? [event.team_id] : []);

        const eventType = event.type ? event.type.toLowerCase() : '';

        return teamIds.includes(parseInt(teamId)) &&
               (eventType === 'scrim' || eventType === 'tournament') &&
               event.score;
      });

      setEvents(teamEvents);
      calculateStats(teamEvents);
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
      alert('Erreur lors du chargement des statistiques');
      navigate('/teams');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (teamEvents) => {
    let wins = 0;
    let losses = 0;
    let draws = 0;
    const recentResults = [];

    teamEvents.forEach(event => {
      if (!event.score) return;

      // Parser le score (ex: "2-1", "16-14", "2/4")
      const scoreParts = event.score.split(/[-\/]/).map(s => parseInt(s.trim()));
      if (scoreParts.length !== 2 || isNaN(scoreParts[0]) || isNaN(scoreParts[1])) return;

      const [ourScore, opponentScore] = scoreParts;

      let result = 'draw';
      if (ourScore > opponentScore) {
        wins++;
        result = 'win';
      } else if (ourScore < opponentScore) {
        losses++;
        result = 'loss';
      } else {
        draws++;
      }

      recentResults.push({
        id: event.id,
        title: event.title,
        opponent: event.opponent || 'Adversaire inconnu',
        score: event.score,
        result: result,
        date: new Date(event.start_datetime)
      });
    });

    // Trier par date décroissante
    recentResults.sort((a, b) => b.date - a.date);

    const totalMatches = wins + losses + draws;
    const winRate = totalMatches > 0 ? ((wins / totalMatches) * 100).toFixed(1) : 0;

    setStats({
      totalMatches,
      wins,
      losses,
      draws,
      winRate,
      recentResults: recentResults.slice(0, 10) // Garder les 10 derniers
    });
  };

  const getResultClass = (result) => {
    if (result === 'win') return 'result-win';
    if (result === 'loss') return 'result-loss';
    return 'result-draw';
  };

  const getResultIcon = (result) => {
    if (result === 'win') return '✅';
    if (result === 'loss') return '❌';
    return '➖';
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="team-statistics-page loading">Chargement...</div>;
  }

  if (!team) {
    return <div className="team-statistics-page">Équipe introuvable</div>;
  }

  return (
    <div className="team-statistics-page">
      <div className="stats-header">
        <button className="btn-back" onClick={() => navigate('/teams')}>
          <ArrowLeftIcon size={18} /> Retour aux équipes
        </button>
        <h1>Statistiques - {team.name}</h1>
      </div>

      {stats.totalMatches === 0 ? (
        <div className="empty-state">
          <p>Aucun match avec score enregistré pour cette équipe.</p>
          <p className="hint">Les statistiques apparaîtront une fois que des scores seront ajoutés aux événements de type Scrim ou Tournoi.</p>
        </div>
      ) : (
        <>
          {/* Cartes de statistiques globales */}
          <div className="stats-overview">
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <div className="stat-value">{stats.totalMatches}</div>
                <div className="stat-label">Matchs joués</div>
              </div>
            </div>

            <div className="stat-card win">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <div className="stat-value">{stats.wins}</div>
                <div className="stat-label">Victoires</div>
              </div>
            </div>

            <div className="stat-card loss">
              <div className="stat-icon">❌</div>
              <div className="stat-content">
                <div className="stat-value">{stats.losses}</div>
                <div className="stat-label">Défaites</div>
              </div>
            </div>

            {stats.draws > 0 && (
              <div className="stat-card draw">
                <div className="stat-icon">➖</div>
                <div className="stat-content">
                  <div className="stat-value">{stats.draws}</div>
                  <div className="stat-label">Nuls</div>
                </div>
              </div>
            )}

            <div className="stat-card primary">
              <div className="stat-icon">🎯</div>
              <div className="stat-content">
                <div className="stat-value">{stats.winRate}%</div>
                <div className="stat-label">Taux de victoire</div>
              </div>
            </div>
          </div>

          {/* Graphique simple de progression */}
          <div className="stats-chart-card">
            <h3>Évolution des résultats</h3>
            <div className="results-timeline">
              {stats.recentResults.slice(0, 10).reverse().map((result, index) => (
                <div key={index} className={`timeline-dot ${getResultClass(result.result)}`} title={`${result.opponent} - ${result.score}`}>
                  {getResultIcon(result.result)}
                </div>
              ))}
            </div>
            <div className="timeline-labels">
              <span>Plus ancien</span>
              <span>Plus récent</span>
            </div>
          </div>

          {/* Liste des matchs récents */}
          <div className="recent-matches-card">
            <h3>Matchs récents</h3>
            <div className="matches-list">
              {stats.recentResults.map((result) => (
                <div
                  key={result.id}
                  className={`match-item ${getResultClass(result.result)}`}
                  onClick={() => navigate(`/events/${result.id}`)}
                >
                  <div className="match-result-icon">
                    {getResultIcon(result.result)}
                  </div>
                  <div className="match-info">
                    <div className="match-title">{result.title}</div>
                    <div className="match-opponent">vs {result.opponent}</div>
                  </div>
                  <div className="match-score">{result.score}</div>
                  <div className="match-date">{formatDate(result.date)}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TeamStatistics;
